import { useAuthHelper } from "@/components/auth/helper";
import PaymentRepository from "@/utilities/repositories/Payment";
import UserRepository from "@/utilities/repositories/Users";
import { EFeatureType } from "@/utilities/types/AdvanceFeature";
import { IUserData, IUserProfileStore, Roles } from "@/utilities/types/Users";
import { atom, useAtomValue, useSetAtom } from "jotai";
import { useCallback, useEffect, useState } from "react";
import useAuthHeader from "react-auth-kit/hooks/useAuthHeader";
import useSignIn from "react-auth-kit/hooks/useSignIn";
import { useAdvanceFeatures } from "./use-advance-features";
import UserFlagRepository from "@/utilities/repositories/UserFlag";
import useApi from "./useApi";
import { IResponseData } from "@/utilities/types/requests";
import { EUserFlagType, IUserFlag } from "@/utilities/types/UserFlag";
import { useLocation, useNavigate } from "react-router-dom";
import { routes } from "@/config/routes";
import { EXeroPaymentStatus } from "@/utilities/types/Payment";
import { isEmpty } from "lodash";
import dayjs from "dayjs";
import SystemSettingRepository from "@/utilities/repositories/SystemSetting";
import { EUserSuspendStatus } from "@/utilities/types/suspension";
import { EDefaultUserSettingRoute } from "@/utilities/enums/DefaultUserSetting";

export const profileAtom = atom<IUserProfileStore | null>(
  null as IUserData | null
);
export default function useProfile() {
  const profile = useAtomValue(profileAtom);
  const setProfile = useSetAtom(profileAtom);

  const [isFetched, setIsFetched] = useState(false);
  const [loading, setLoading] = useState(false);
  const authHeader = useAuthHeader();
  const signIn = useSignIn<IUserData>();
  const { clearAuth } = useAuthHelper();

  const {
    setEnabledLayout,
    setTelegramFeature,
    setDynamicLayoutMode,
    setLandingPageFeature,
    setMultipleContact,
    setMultipleAccount,
  } = useAdvanceFeatures();

  const { request: getMeUserFlag } = useApi<IResponseData<IUserFlag[]>>({
    request: UserFlagRepository.getMe,
  });

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const fetchData = async () => {
      if (!authHeader || profile || loading) return;

      try {
        setLoading(true);
        const [profileRes, systemSetting] = await Promise.all([
          UserRepository.getMe(),
          SystemSettingRepository.getKillSwitch(),
        ]);

        if (
          profileRes.data?.role !== Roles.ADMIN &&
          systemSetting?.data?.status === "active"
        ) {
          await clearAuth(); // clear auth if system under maintenance
        }
        const paymentRes =
          profileRes.data?.role !== Roles.ADMIN
            ? await PaymentRepository.getPaymentIsPendingByUser()
            : null;

        if (
          profileRes.data?.role !== Roles.ADMIN &&
          profileRes.data?.role !== Roles.USER
        ) {
          const userFlag = await getMeUserFlag({
            type: EUserFlagType.PAYMENT_CONFIRMED,
          });
          const paymentId = userFlag?.data.at(0)?.data?.paymentId;
          // to verify payment flow
          if (paymentId) {
            navigate(routes.confirmPayment, { state: { paymentId } });
          }
        }

        const userPackage = profileRes?.data?.userPackage?.at(0);

        const isExpiredPackage = dayjs().isAfter(
          dayjs(userPackage?.expiredAt || "")
        );

        const isExistPendingPayment =
          isEmpty(profileRes.data.userPackage) && !paymentRes?.data;

        const isVerifying =
          !!paymentRes?.data?.assetId &&
          paymentRes?.data?.status === EXeroPaymentStatus.PENDING; //wait for admin to verify

        const mergedProfile: IUserProfileStore = {
          ...profileRes.data,
          isExistPendingPayment,
          isVerifying,
          isExpiredPackage,
        };

        if (mergedProfile.userFeatures) {
          localStorage.setItem(
            "userFeatures",
            JSON.stringify(mergedProfile.userFeatures)
          );
        }

        setProfile(mergedProfile);
        setLoading(false);
      } catch (err) {
        console.error("Failed to fetch profile/payment info:", err);
      }
    };

    fetchData();
  }, [authHeader, profile, setProfile, loading]);

  const updateLayoutState = useCallback(() => {
    if (!profile?.userFeatures) return;

    const findFeature = (type: EFeatureType) =>
      profile?.userFeatures?.find((f) => f.feature?.type === type);

    setTelegramFeature(findFeature(EFeatureType.IpAccessTrackerTelegram));
    setLandingPageFeature(findFeature(EFeatureType.LandingPage));
    setMultipleContact(findFeature(EFeatureType.MultipleContact));
    setMultipleAccount(findFeature(EFeatureType.TeamAccount));

    const dynamicEnabled = findFeature(EFeatureType.DynamicLayout)?.enabled;
    const dynamicMode = profile.dynamicLayoutMode;

    setEnabledLayout(!!dynamicEnabled);
    if (dynamicMode) setDynamicLayoutMode(dynamicMode);
  }, [profile]);

  useEffect(() => {
    updateLayoutState();
  }, [updateLayoutState]);

  useEffect(() => {
    if (
      profile?.userSuspends &&
      profile.userSuspends.length > 0 &&
      profile.userSuspends[0].status === EUserSuspendStatus.ACTIVE &&
      profile.role !== Roles.ADMIN
    ) {
      navigate(routes.websiteSuspended);
    }
  }, [profile?.id]);

  const handleSetProfile = (userData: IUserData) => {
    const token = authHeader?.split(" ").pop() ?? "";

    signIn({
      auth: { token, type: "Bearer" },
      userState: userData,
    });

    setProfile(userData);

    if (userData?.userFeatures) {
      localStorage.setItem(
        "userFeatures",
        JSON.stringify(userData.userFeatures)
      );
    }
  };

  return {
    profile,
    setProfile: handleSetProfile,
    loading,
  };
}
